import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canceler } from "axios";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Button,
    HStack,
    VStack,
    useDisclosure,
    useToast,
    Text,
    Box,
    Hide,
} from "@chakra-ui/react";
import { AppSearch } from "../../../containers";
import {
    AppAlert,
    AppFilterButton,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
    AppPagination,
    AppWidgetCard,
} from "../../../components";
import { PartnershipSchoolFilter as FilterComponent } from "./PartnershipSchoolFilter";
import {
    School as Entity,
    SchoolApi as EntityApi,
    Partnership,
    PartnershipApi,
} from "../../../apis";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC, cPath, UTIL } from "../../../config";

export const PartnershipSchoolPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const navigate = useNavigate();
    const { grant } = useAuthData();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const {
        isOpen: isOpenChangeStatus,
        onOpen: onOpenChangeStatus,
        onClose: onCloseChangeStatus,
    } = useDisclosure();

    // state & const
    const [search, setSearch] = useState("");
    const [localSearch, setLocalSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const cancelTokenSourcesRef = useRef<Canceler[]>([]);
    const [page, setPage] = useState(1);
    const [pagesCount, setPagesCount] = useState(0);
    const [partnershipStatusList, setPartnershipStatusList] = useState<
        Record<string | number, Partnership>
    >({});
    const [partnershipId, setPartnershipId] = useState<string>("");
    const [partnershipStatus, setPartnershipStatus] = useState<string>("");
    const pageSize = APPC.PAGE_SIZE;
    const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;
    const dayjs = UTIL.getDayJs();

    const isRequest =
        window.location.pathname ===
        cPath("ADMIN.REQUEST_PARTNERSHIP_SCHOOL_PAGE", {});

    // handler
    const fetchListData = () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            "order[name]": "asc",
            ["partnerships.company.id"]: activeSchoolOrCompany?.["@id"] ?? "",
            ...filterData,
        };
        if (search) {
            params["school_search"] = search;
        }
        if (localSearch !== search) {
            setLocalSearch(search);
            if (page != 1) {
                setPage(1);
                return;
            }
        }
        if (isRequest) {
            params["partnerships.status"] = [
                PARTNERSHIPSTATUS.STATUS_PENDING,
                PARTNERSHIPSTATUS.STATUS_REJECTED,
            ];
        } else {
            params["partnerships.status"] = PARTNERSHIPSTATUS.STATUS_APPROVED;
        }

        EntityApi.getCollectionMyPartnership<Entity>(page, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const fetchPartnershipStatus = () => {
        setLoadingStatus(true);
        const params = {
            pagination: false,
            ["company.id"]: activeSchoolOrCompany?.["@id"] ?? "",
        };

        PartnershipApi.getCollectionMyPartnershipsStatus<Partnership>(
            1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    const transformedStatus: Record<
                        string | number,
                        Partnership
                    > = {};
                    response.items.forEach((item) => {
                        const schoolId =
                            String(item.school?.["@id"]).split("/").pop() || "";
                        transformedStatus[schoolId] = item;
                    });
                    setPartnershipStatusList(transformedStatus);
                }
            })
            .finally(() => setLoadingStatus(false));
    };

    const handleChangePartnershipStatus = async (
        partnershipId: string,
        status: string
    ) => {
        PartnershipApi.patchPartnershipChangeStatus<
            Partnership,
            { status: string }
        >(partnershipId, { status }).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                if (response.status === PARTNERSHIPSTATUS.STATUS_APPROVED) {
                    toast({
                        title: t(
                            "padm.PartnershipSchoolPage:toast.success.partnership"
                        ),
                        status: "success",
                    });
                } else if (
                    response.status === PARTNERSHIPSTATUS.STATUS_REJECTED
                ) {
                    toast({
                        title: t(
                            "padm.PartnershipSchoolPage:toast.fail.partnership"
                        ),
                        status: "success",
                    });
                }
                const schoolId = String(response.school).split("/").pop() || "";
                setPartnershipStatusList((prev) => ({
                    ...prev,
                    [schoolId]: {
                        ...prev[schoolId],
                        status: response.status,
                    } as Partnership,
                }));
                fetchListData();
                fetchPartnershipStatus();
            }
        });
    };

    const renderChangeStatusAlert = () => {
        return (
            <AppAlert
                title={
                    partnershipStatus === PARTNERSHIPSTATUS.STATUS_APPROVED
                        ? t(
                              "padm.PartnershipSchoolPage:alert.changeStatus.accept.title"
                          )
                        : t(
                              "padm.PartnershipSchoolPage:alert.changeStatus.reject.title"
                          )
                }
                isOpen={isOpenChangeStatus}
                onSuccess={() =>
                    handleChangePartnershipStatus(
                        partnershipId,
                        partnershipStatus
                    )
                }
                onCancel={onCloseChangeStatus}
                successLable={t(
                    "padm.PartnershipSchoolPage:alert.changeStatus.button.success"
                )}
                cancelLable={t(
                    "padm.PartnershipSchoolPage:alert.changeStatus.button.cancel"
                )}
                size={"lg"}
            >
                <VStack py={6}>
                    <AppIcon
                        name={
                            partnershipStatus ===
                            PARTNERSHIPSTATUS.STATUS_APPROVED
                                ? "icl-check"
                                : "icl-x"
                        }
                        w="2.5rem"
                        color={
                            partnershipStatus ===
                            PARTNERSHIPSTATUS.STATUS_APPROVED
                                ? "var(--chakra-colors-primary)"
                                : "var(--chakra-colors-destructive)"
                        }
                    />
                    {partnershipStatus === PARTNERSHIPSTATUS.STATUS_APPROVED ? (
                        <>
                            <Text textStyle={"boldMd"}>
                                {t(
                                    "padm.PartnershipSchoolPage:alert.changeStatus.accept.message"
                                )}
                            </Text>
                            <Text textStyle={"regularSm"}>
                                {t(
                                    "padm.PartnershipSchoolPage:alert.changeStatus.accept.detail"
                                )}
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text textStyle={"boldMd"}>
                                {t(
                                    "padm.PartnershipSchoolPage:alert.changeStatus.reject.message"
                                )}
                            </Text>
                            <Text textStyle={"regularSm"}>
                                {t(
                                    "padm.PartnershipSchoolPage:alert.changeStatus.reject.detail"
                                )}
                            </Text>
                        </>
                    )}
                </VStack>
            </AppAlert>
        );
    };

    // useEffect
    useEffect(() => {
        fetchListData();
    }, [page, search, isRequest, activeSchoolOrCompany, filterData]);

    useEffect(() => {
        fetchPartnershipStatus();
    }, [activeSchoolOrCompany, isRequest]);

    useEffect(() => {
        if (page != 1) {
            setPage(1);
        }
    }, [filterData]);

    return (
        <>
            <AppPageHeader
                title={
                    isRequest
                        ? t(
                              "padm.PartnershipSchoolPage:text.partnershipSchoolRequestPageTitle"
                          )
                        : t(
                              "padm.PartnershipSchoolPage:text.partnershipSchoolPageTitle"
                          )
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <>
                        <AppSearch onSearch={(value) => setSearch(value)} />
                        <FilterComponent
                            filterData={filterData}
                            setFilterData={setFilterData}
                        >
                            <Box role="button">
                                <AppFilterButton filterData={filterData} />
                            </Box>
                        </FilterComponent>
                    </>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                {loadingList || loadingStatus ? (
                    <AppLoader />
                ) : (
                    <>
                        <HStack
                            px={{ base: 3, sm: 6 }}
                            pt={{ base: 3.5, sm: 4, md: "1.125rem" }}
                            gap={0}
                        >
                            <Button
                                size={"sm"}
                                variant="tab"
                                isActive={!isRequest}
                                onClick={() =>
                                    navigate(
                                        cPath("ADMIN.PARTNERSHIP_SCHOOL_PAGE")
                                    )
                                }
                            >
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t(
                                            "padm.PartnershipSchoolPage:text.partnershipSchoolPageTitle"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                            <Button
                                size={"sm"}
                                variant="tab"
                                isActive={isRequest}
                                onClick={() =>
                                    navigate(
                                        cPath(
                                            "ADMIN.REQUEST_PARTNERSHIP_SCHOOL_PAGE"
                                        )
                                    )
                                }
                            >
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t(
                                            "padm.PartnershipSchoolPage:text.partnershipSchoolRequestPageTitle"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                        </HStack>
                        <VStack p={6} gap={6} alignItems={"stretch"}>
                            {list.map((item) => (
                                <AppWidgetCard
                                    key={item.id}
                                    data={item}
                                    partnership={
                                        !isRequest
                                            ? partnershipStatusList[item?.id]
                                            : undefined
                                    }
                                    actionElement={
                                        Object.keys(
                                            partnershipStatusList
                                        ).includes(String(item?.id)) &&
                                        partnershipStatusList[item?.id]
                                            ?.status ===
                                            PARTNERSHIPSTATUS.STATUS_PENDING ? (
                                            <VStack
                                                alignItems={"flex-end"}
                                                gap={1.5}
                                            >
                                                <Text textStyle={"regularXs"}>
                                                    {`${t(
                                                        "padm.PartnershipSchoolPage:text.requestReceivedAt"
                                                    )} ${dayjs(
                                                        partnershipStatusList[
                                                            item?.id
                                                        ]?.createdAt
                                                    ).format(
                                                        APPC.APP_DATEANDTIME_FORMAT
                                                    )}`}
                                                </Text>
                                                <HStack gap={4}>
                                                    <Button
                                                        size={"sm"}
                                                        variant={"destructive"}
                                                        onClick={() => {
                                                            setPartnershipId(
                                                                String(
                                                                    partnershipStatusList[
                                                                        item?.id
                                                                    ]?.id
                                                                )
                                                            );
                                                            setPartnershipStatus(
                                                                PARTNERSHIPSTATUS.STATUS_REJECTED
                                                            );
                                                            onOpenChangeStatus();
                                                        }}
                                                        isDisabled={
                                                            grant.isCompanyDirector
                                                        }
                                                    >
                                                        {t(
                                                            "padm.PartnershipSchoolPage:button.reject"
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size={"sm"}
                                                        onClick={() => {
                                                            setPartnershipId(
                                                                String(
                                                                    partnershipStatusList[
                                                                        item?.id
                                                                    ]?.id
                                                                )
                                                            );
                                                            setPartnershipStatus(
                                                                PARTNERSHIPSTATUS.STATUS_APPROVED
                                                            );
                                                            onOpenChangeStatus();
                                                        }}
                                                        isDisabled={
                                                            grant.isCompanyDirector
                                                        }
                                                    >
                                                        {t(
                                                            "padm.PartnershipSchoolPage:button.accept"
                                                        )}
                                                    </Button>
                                                </HStack>
                                            </VStack>
                                        ) : partnershipStatusList[item?.id]
                                              ?.status ===
                                          PARTNERSHIPSTATUS.STATUS_REJECTED ? (
                                            <VStack
                                                alignItems={"flex-end"}
                                                gap={1.5}
                                            >
                                                <Text textStyle={"regularXs"}>
                                                    {`${t(
                                                        "padm.PartnershipSchoolPage:text.requestReceivedAt"
                                                    )} ${dayjs(
                                                        partnershipStatusList[
                                                            item?.id
                                                        ]?.createdAt
                                                    ).format(
                                                        APPC.APP_DATEANDTIME_FORMAT
                                                    )}`}
                                                </Text>
                                                <Button
                                                    size={"sm"}
                                                    onClick={() => {
                                                        setPartnershipId(
                                                            String(
                                                                partnershipStatusList[
                                                                    item?.id
                                                                ]?.id
                                                            )
                                                        );
                                                        setPartnershipStatus(
                                                            PARTNERSHIPSTATUS.STATUS_APPROVED
                                                        );
                                                        onOpenChangeStatus();
                                                    }}
                                                >
                                                    {t(
                                                        "padm.PartnershipSchoolPage:button.accept"
                                                    )}
                                                </Button>
                                            </VStack>
                                        ) : (
                                            <></>
                                        )
                                    }
                                />
                            ))}
                            {pagesCount > 1 && list.length > 0 && (
                                <AppPagination
                                    onClick={(pageNumber) =>
                                        setPage(pageNumber)
                                    }
                                    totalPages={pagesCount}
                                    activePage={page}
                                />
                            )}
                        </VStack>
                        {isOpenChangeStatus && renderChangeStatusAlert()}
                    </>
                )}
            </AppPageContent>
        </>
    );
};
